import React, { useState, useEffect } from "react";
import { Table, Button, Input, Form, message, Modal, Card, Spin, Tooltip, Space } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { confirm } = Modal;

const ColorManager = () => {
  const [colors, setColors] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch colors from the API
  useEffect(() => {
    const fetchColors = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/product/colors");
        if (response.data.success) {
          const formattedColors = response.data.colors.map((clr) => ({
            key: clr.id.toString(),
            name: clr.color_name,
          }));
          setColors(formattedColors);
        } else {
          message.error("Failed to fetch colors.");
        }
      } catch (error) {
        message.error("An error occurred while fetching colors.");
      } finally {
        setLoading(false);
      }
    };

    fetchColors();
  }, []);

  // Handle adding a new color
  const handleAddColor = async (values) => {
    try {
      const response = await axiosInstance.post("/admin/color", {
        color_name: values.color_name,
      });

      if (response.status === 201 && response.data.success) {
        const newColor = {
          key: response.data.color.id.toString(),
          name: response.data.color.color_name,
        };

        setColors((prevColors) => [...prevColors, newColor]);
        message.success("Color added successfully!");
        setAddModalVisible(false);
        form.resetFields();
      } else {
        message.error("Failed to add color.");
      }
    } catch (error) {
      message.error("An error occurred while adding the color.");
    }
  };

  // Handle deleting a color with confirmation
  const handleDeleteColor = (key) => {
    confirm({
      title: "Are you sure you want to delete this color?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          const response = await axiosInstance.delete(`/admin/color/${key}`);
          if (response.data.success) {
            setColors((prev) => prev.filter((clr) => clr.key !== key));
            message.success("Color deleted successfully!");
          } else {
            message.error("Failed to delete color.");
          }
        } catch (error) {
          message.error("An error occurred while deleting the color.");
        }
      },
    });
  };

  // Filter colors by search text
  const filteredColors = colors.filter((color) =>
    color.name.toLowerCase().includes(searchText.toLowerCase())
  );

  // Table columns with Sr.No and delete button with tooltip
  const columns = [
    {
      title: "Sr.No",
      key: "srno",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Color Name",
      dataIndex: "name",
      key: "name",
      align: "left",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="Delete">
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteColor(record.key)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* Inline Responsive Styles */}
      <style>{`
        .color-manager-container {
          padding: 20px;
          background: #fff;
          border-radius: 10px;
          width: 100%;
          max-width: 1200px;
          box-sizing: border-box;
          margin: auto;
          position: relative;
        }
        
        .header-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .header-controls h2 {
          margin: 0;
          font-size: 24px;
          line-height: 32px;
        }
        
        .filter-controls {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }
        
        @media (max-width: 600px) {
          .header-controls {
            flex-direction: column;
            align-items: flex-start;
          }
          .filter-controls {
            flex-direction: column;
            align-items: stretch;
          }
          .filter-controls > * {
            width: 100% !important;
          }
        }
      `}</style>

      <Card className="color-manager-container">
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255,255,255,0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <Spin size="large" tip="Loading colors..." />
          </div>
        )}
        <div className="header-controls">
          <h2>Manage Colors</h2>
          <Button type="primary" onClick={() => setAddModalVisible(true)}>
            Add New Color
          </Button>
        </div>

        <div className="filter-controls">
          <Input
            placeholder="Search Color..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300, borderRadius: "5px" }}
          />
        </div>

        <div style={{ overflowX: "auto" }}>
          <Table
            dataSource={filteredColors}
            columns={columns}
            rowKey="key"
            pagination={filteredColors.length >= 10 ? { pageSize: 10 } : false}
            bordered
          />
        </div>

        <Modal
          title="Add New Color"
          open={addModalVisible}
          onCancel={() => setAddModalVisible(false)}
          footer={null}
        >
          <Form form={form} layout="vertical" onFinish={handleAddColor}>
            <Form.Item
              label="Color Name"
              name="color_name"
              rules={[{ required: true, message: "Please enter a color name!" }]}
            >
              <Input placeholder="Enter color name" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Add Color
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </>
  );
};

export default ColorManager;
